import { motion } from 'framer-motion';
import {
  Car,
  ChevronsLeft,
  Cpu,
  IdCard,
  LayoutDashboard,
  Map,
  MapPinned,
  Nfc,
  PanelLeft,
  Pencil,
  Receipt,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Brand, BrandMark } from '@/components/layout/brand';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { fullName, humanizeEnum, initials } from '@/lib/format';
import { useAlertSummary } from '@/lib/queries/alerts';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useUiStore } from '@/stores/ui-store';
import type { Permission } from '@/types/api';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Hidden entirely when the signed-in user lacks this. */
  permission?: Permission;
  badge?: 'alerts';
}

const OPERATIONS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/map', label: 'Live map', icon: Map },
  { to: '/alerts', label: 'Alerts', icon: ShieldAlert, badge: 'alerts' },
  { to: '/geofences', label: 'Geofences', icon: MapPinned, permission: 'geofence:read' },
];

const REVENUE: NavItem[] = [
  { to: '/drivers', label: 'Drivers', icon: IdCard, permission: 'vehicle:read' },
  { to: '/transactions', label: 'Transactions', icon: Receipt, permission: 'payment:read' },
];

const MANAGE: NavItem[] = [
  { to: '/admin/vehicles', label: 'Vehicles', icon: Car, permission: 'vehicle:read' },
  { to: '/admin/devices', label: 'Devices', icon: Cpu, permission: 'device:read' },
  { to: '/admin/sims', label: 'SIM cards', icon: Nfc, permission: 'device:read' },
  { to: '/admin/users', label: 'Users', icon: Users, permission: 'user:read' },
  { to: '/admin/roles', label: 'Roles', icon: ShieldCheck, permission: 'role:read' },
  { to: '/admin/audit', label: 'Audit log', icon: ScrollText, permission: 'audit:read' },
];

const UTILITY: NavItem[] = [{ to: '/settings', label: 'Settings', icon: Settings }];

export const SIDEBAR_WIDTH = 268;
export const SIDEBAR_WIDTH_COLLAPSED = 76;

/**
 * Persistent left rail. Collapses to an icon-only strip, which is what the
 * toggle beside the wordmark does. Route access is filtered by the same
 * permissions the router guards with, so nothing is shown that would 403.
 */
export function Sidebar({
  onNavigate,
  forceExpanded = false,
}: {
  onNavigate?: () => void;
  /** The mobile sheet is always full width, whatever the rail is set to. */
  forceExpanded?: boolean;
}) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed) && !forceExpanded;
  const toggle = useUiStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  const { data: summary } = useAlertSummary();

  const allowed = (item: NavItem) => {
    if (!item.permission || !user) return true;
    return user.role === 'SUPER_ADMIN' || user.permissions.includes(item.permission);
  };

  const operations = OPERATIONS.filter(allowed);
  const revenue = REVENUE.filter(allowed);
  const manage = MANAGE.filter(allowed);

  return (
    <aside
      className="flex h-full shrink-0 flex-col border-r bg-sidebar transition-[width] duration-250 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
      style={{ width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH }}
    >
      <div
        className={cn(
          'flex h-16 shrink-0 items-center',
          collapsed ? 'justify-center px-2' : 'justify-between px-4',
        )}
      >
        {collapsed ? (
          // The only way back out of the collapsed rail, so it lives in the
          // fixed header rather than the scrolling nav below it.
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggle}
                aria-label="Expand sidebar"
                className="group relative flex size-10 items-center justify-center rounded-lg transition-colors hover:bg-card"
              >
                <BrandMark className="size-8 transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0" />
                <PanelLeft className="absolute size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        ) : (
          <>
            <Brand />
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground"
              onClick={toggle}
              aria-label="Collapse sidebar"
            >
              <ChevronsLeft className="size-4" />
            </Button>
          </>
        )}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3">
        {operations.map((item) => (
          <NavRow
            key={item.to}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
            count={item.badge === 'alerts' ? (summary?.open ?? 0) : undefined}
          />
        ))}

        {revenue.length > 0 && (
          <>
            {collapsed ? (
              <div className="mx-auto my-2 h-px w-8 bg-border" />
            ) : (
              <p className="label-micro px-3 pt-5 pb-1.5">Revenue</p>
            )}
            {revenue.map((item) => (
              <NavRow key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
            ))}
          </>
        )}

        {manage.length > 0 && (
          <>
            {collapsed ? (
              <div className="mx-auto my-2 h-px w-8 bg-border" />
            ) : (
              <p className="label-micro px-3 pt-5 pb-1.5">Manage</p>
            )}
            {manage.map((item) => (
              <NavRow key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
            ))}
          </>
        )}

        <div className="flex-1" />

        {collapsed ? (
          <div className="mx-auto mb-2 h-px w-8 bg-border" />
        ) : (
          <div className="my-2 h-px bg-border" />
        )}

        {UTILITY.map((item) => (
          <NavRow key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
      </nav>

      {user && (
        <div className="shrink-0 border-t p-3">
          <NavLink
            to="/settings"
            onClick={onNavigate}
            className={cn(
              'group flex items-center gap-2.5 rounded-lg p-1.5 transition-colors hover:bg-card',
              collapsed && 'justify-center',
            )}
          >
            <Avatar className="size-9 rounded-lg">
              <AvatarFallback className="rounded-lg bg-primary-soft text-[11px] font-bold text-primary">
                {initials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-[13px] font-semibold">{fullName(user)}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {humanizeEnum(user.role)}
                  </span>
                </span>
                <Pencil className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </>
            )}
          </NavLink>
        </div>
      )}
    </aside>
  );
}

function NavRow({
  item,
  collapsed,
  count,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  count?: number;
  onNavigate?: () => void;
}) {
  const row = (
    <NavLink to={item.to} onClick={onNavigate} end={item.to === '/map'}>
      {({ isActive }) => (
        <span
          className={cn(
            'relative flex h-10 items-center rounded-lg text-[13.5px] font-medium transition-colors',
            collapsed ? 'justify-center px-0' : 'gap-3 px-3',
            isActive
              ? 'nav-active'
              : 'text-muted-foreground hover:bg-card/70 hover:text-foreground',
          )}
        >
          {isActive && (
            <motion.span
              layoutId="sidebar-active"
              className="absolute inset-0 rounded-lg bg-card shadow-card"
              transition={{ type: 'spring', stiffness: 520, damping: 42 }}
            />
          )}
          <item.icon
            className={cn(
              'relative size-[18px] shrink-0',
              isActive ? 'text-primary' : 'text-current',
            )}
            strokeWidth={2}
          />
          {!collapsed && <span className="relative flex-1 truncate">{item.label}</span>}
          {!collapsed && count !== undefined && count > 0 && (
            <span className="tnum relative rounded-full bg-alert-soft px-1.5 py-0.5 text-[10px] font-bold text-alert">
              {count > 99 ? '99+' : count}
            </span>
          )}
          {collapsed && count !== undefined && count > 0 && (
            <span className="absolute top-1 right-1 size-2 rounded-full bg-alert" />
          )}
        </span>
      )}
    </NavLink>
  );

  if (!collapsed) return row;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}
